import { tf, tr } from "../i18n";
import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { RoomEvent, type MatrixClient, type Room } from "matrix-js-sdk";
import {
  MatrixRTCSessionEvent,
  MatrixRTCSessionManagerEvents,
  type MatrixRTCSession,
} from "matrix-js-sdk/lib/matrixrtc/index.js";
import { managerFor } from "../matrix/rtc";
import { isEncrypted } from "../matrix/rooms";
import { startRinging, stopRinging } from "./callRing";
import { loadNotificationSettings } from "./settings";
import { declineActiveCall } from "./declineCall";
import { clearSelfLeft, isIncomingSuppressed } from "./selfLeftCalls";
import { deliverSystemNotification, type AppNotificationOptions } from "./systemNotification";

export const INCOMING_CALL_TTL_MS = 60_000;

export interface IncomingCall {
  roomId: string;
  roomName: string;
  callers: string;
  participants: number;
  isVideo: boolean;
  notificationEventId: string | null;
  /** E2EE calls are informational only: no accept action, ring, or OS notification. */
  unsupported: boolean;
  unsupportedReason: string | null;
}

export interface IncomingCallContext {
  openRoom(roomId: string): void;
}

export function incomingCallTitle(call: IncomingCall): string {
  return call.isVideo
    ? tf("call.ongoingVideoInRoom", { room: call.roomName })
    : tf("call.ongoingAudioInRoom", { room: call.roomName });
}

let activeNotification: Notification | null = null;
let activeServiceWorkerTag: string | null = null;
let activeNativeTag: string | null = null;

function closeNotification(): void {
  activeNotification?.close();
  activeNotification = null;
  const nativeTag = activeNativeTag;
  activeNativeTag = null;
  if (nativeTag && window.ALGDesktop?.closeNotification) {
    try {
      void Promise.resolve(window.ALGDesktop.closeNotification(nativeTag)).catch(() => {});
    } catch {
      // Closing the page/service-worker copies below must still proceed.
    }
  }
  const tag = activeServiceWorkerTag;
  activeServiceWorkerTag = null;
  if (tag && "serviceWorker" in navigator) {
    void navigator.serviceWorker.getRegistration().then(async (registration) => {
      if (!registration) return;
      const notifications = await registration.getNotifications({ tag });
      notifications.forEach((notification) => notification.close());
    }).catch(() => {});
  }
}

async function showIncomingNotification(
  call: IncomingCall,
  ctxRef: MutableRefObject<IncomingCallContext>,
): Promise<boolean> {
  if (!window.ALGDesktop?.showNotification && !("serviceWorker" in navigator) && typeof Notification === "undefined") return false;
  const title = call.isVideo
    ? tf("call.incomingVideoFrom", { callers: call.callers })
    : tf("call.incomingAudioFrom", { callers: call.callers });
  const tag = `alg-call-${call.roomId}`;
  const options: AppNotificationOptions = {
    body: tf("call.roomOngoingParticipants", {
      room: call.roomName,
      participants: call.participants,
    }),
    tag,
    requireInteraction: true,
    renotify: true,
    silent: false,
    data: {
      url: `${window.location.origin}${window.location.pathname}#/room/${encodeURIComponent(call.roomId)}`,
    },
  };

  try {
    const delivery = await deliverSystemNotification(title, options, (notification) => {
      window.focus();
      ctxRef.current.openRoom(call.roomId);
      notification.close();
    });
    if (delivery.channel === "desktop-native") activeNativeTag = tag;
    if (delivery.channel === "application-service") activeServiceWorkerTag = tag;
    if (delivery.notification) {
      const notification = delivery.notification;
      notification.onclose = () => {
        if (activeNotification === notification) activeNotification = null;
      };
      activeNotification = notification;
    }
    return true;
  } catch (error) {
    console.warn("[call] system notification failed:", error);
    return false;
  }
}

function describe(client: MatrixClient, room: Room, session: MatrixRTCSession): IncomingCall | null {
  const me = client.getUserId();
  if (!me || session.isJoined()) return null;

  const alive = session.memberships.filter((membership) => !membership.isExpired());
  // A call started by another one of our devices is still our own call.
  if (alive.some((membership) => membership.userId === me)) return null;
  const foreign = alive.filter((membership) => membership.userId !== me);
  // СЕССИЯ ЗАКОНЧИЛАСЬ ПОЛНОСТЬЮ — снимаем отметку о своём выходе.
  //
  // Это ЕДИНСТВЕННОЕ место, где она снимается по естественному завершению,
  // и оно критично: отметка без снятия = комната НАВСЕГДА перестаёт звонить.
  // Условие «живых чужих членств нет» и есть «звонка больше не существует»,
  // ровно как обещает шапка selfLeftCalls.ts. Второй путь снятия —
  // clearSelfLeftOnRejoin при моём повторном входе (rtc.ts:1620).
  if (!foreign.length) {
    clearSelfLeft(room.roomId);
    return null;
  }

  let latest = 0;
  let isVideo = false;
  let notificationEventId: string | null = null;
  let callId: string | null = null;
  for (const event of room.getLiveTimeline().getEvents().slice(-40)) {
    if (event.getType() !== "org.matrix.msc4075.rtc.notification") continue;
    const content = event.getContent() as Record<string, unknown>;
    const timestamp = typeof content.sender_ts === "number" ? content.sender_ts : event.getTs();
    const lifetime = typeof content.lifetime === "number" ? content.lifetime : 0;
    if (lifetime && Date.now() > timestamp + lifetime) continue;
    if (timestamp >= latest) {
      latest = timestamp;
      isVideo = content["m.call.intent"] === "video";
      notificationEventId = event.getId() ?? null;
      callId = typeof content.call_id === "string" ? content.call_id : null;
    }
  }

  // No fresh call-notification event means that memberships alone are not
  // enough to wake the user: they can linger after an interrupted call.
  if (!latest || Date.now() - latest > INCOMING_CALL_TTL_MS) return null;

  // Я САМ ОТКЛОНИЛ ЭТОТ ЗВОНОК ИЛИ ТОЛЬКО ЧТО ВЫШЕЛ ИЗ НЕГО.
  //
  // Проверка стоит ПОСЛЕ вычисления notificationEventId и ДО поиска
  // m.call.hangup, потому что она от сети не зависит вовсе: отказ погашен
  // локально, даже если hangup не ушёл. Раньше читателя у selfLeftCalls не
  // было совсем, и отметка ни на что не влияла — баннер возвращался по
  // таймеру 15 с, по Timeline и по visibilitychange.
  //
  // ПОВТОРНЫЙ ЗВОНОК ПРОБЬЁТСЯ: звонящий публикует НОВОЕ уведомление с
  // новым event id, которого нет в списке погашенных, и с новым sender_ts
  // заведомо позже метки выхода. Блокируется именно отклонённый звонок, а
  // не комната.
  if (isIncomingSuppressed(room.roomId, notificationEventId, latest)) return null;

  // Explicit termination wins over sticky MatrixRTC membership. Without this
  // check remote devices keep ringing until the membership or invite TTL ends.
  for (const event of room.getLiveTimeline().getEvents().slice(-80)) {
    if (event.getType() !== "m.call.hangup" || event.getTs() < latest) continue;
    const content = event.getContent() as Record<string, unknown>;
    const relates = content["m.relates_to"] as Record<string, unknown> | undefined;
    const referencesNotification = notificationEventId
      && relates?.event_id === notificationEventId;
    const matchesCall = callId && content.call_id === callId;
    if (referencesNotification || matchesCall) return null;
  }

  const unsupported = isEncrypted(room);
  return {
    roomId: room.roomId,
    roomName: room.name || room.roomId,
    callers: foreign
      .map((membership) => room.getMember(membership.userId)?.name ?? membership.userId)
      .filter((value, index, all) => all.indexOf(value) === index)
      .slice(0, 3)
      .join(", "),
    participants: alive.length,
    isVideo,
    notificationEventId,
    unsupported,
    unsupportedReason: unsupported ? tr("ui.zvonki_v_zashifrovannyh_komnatah_poka_ne_2") : null,
  };
}

export function useIncomingCall(
  client: MatrixClient | null,
  ctx: IncomingCallContext,
): IncomingCall | null {
  const [incoming, setIncoming] = useState<IncomingCall | null>(null);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  useEffect(() => {
    if (!client) return;
    const manager = managerFor(client);
    const sessions = new Map<string, MatrixRTCSession>();
    let activeRoomId: string | null = null;
    let activeUnsupported = false;
    // UI/ringing identity and system-delivery state are intentionally
    // separate. Previously the call was marked active before the async
    // notification attempt. One transient failure (worker still activating,
    // permission still default) then made every later apply() return early,
    // so that call could never produce a system notification.
    let deliveredCallKey: string | null = null;
    let pendingCallKey: string | null = null;

    const notificationKey = (call: IncomingCall): string =>
      `${call.roomId}:${call.notificationEventId ?? "unknown"}`;

    const trySystemNotification = (call: IncomingCall) => {
      const key = notificationKey(call);
      if (deliveredCallKey === key || pendingCallKey === key) return;
      pendingCallKey = key;
      void showIncomingNotification(call, ctxRef).then((delivered) => {
        if (delivered) deliveredCallKey = key;
      }).finally(() => {
        if (pendingCallKey === key) pendingCallKey = null;
      });
    };

    const apply = () => {
      let next: IncomingCall | null = null;
      for (const [roomId, session] of sessions) {
        const room = client.getRoom(roomId);
        const candidate = room && describe(client, room, session);
        if (candidate && !next) next = candidate;
      }

      // Focus/visibility/interval checks also re-evaluate encryption and TTL.
      if (next?.roomId === activeRoomId && next.unsupported === activeUnsupported) {
        setIncoming(next);
        if (next && !next.unsupported) {
          const settings = loadNotificationSettings();
          if (settings.enabled && settings.categories.incomingCalls) trySystemNotification(next);
        }
        return;
      }

      activeRoomId = next?.roomId ?? null;
      activeUnsupported = next?.unsupported ?? false;
      deliveredCallKey = null;
      pendingCallKey = null;
      stopRinging();
      closeNotification();
      setIncoming(next);
      if (!next || next.unsupported) return;

      const settings = loadNotificationSettings();
      if (!settings.categories.incomingCalls) return;
      // startRinging reads the separately selected settings.callTone.
      if (settings.callSound) startRinging();

      // Keep the global display switch authoritative for system banners as it
      // already is for messages/invites. Sound has its own setting and remains
      // untouched.
      if (settings.enabled) trySystemNotification(next);
    };

    const watch = (room: Room) => {
      if (sessions.has(room.roomId)) return;
      const session = manager.getRoomSession(room);
      sessions.set(room.roomId, session);
      session.on(MatrixRTCSessionEvent.MembershipsChanged, apply);
      session.on(MatrixRTCSessionEvent.JoinStateChanged, apply);
      room.on(RoomEvent.Timeline, apply);
    };

    client.getRooms().filter((room) => room.getMyMembership() === "join").forEach(watch);
    const onManagerSession = (roomId: string) => {
      const room = client.getRoom(roomId);
      if (room?.getMyMembership() === "join") watch(room);
      apply();
    };
    manager.on(MatrixRTCSessionManagerEvents.SessionStarted, onManagerSession);
    manager.on(MatrixRTCSessionManagerEvents.SessionEnded, onManagerSession);

    const tick = window.setInterval(apply, 15_000);
    document.addEventListener("visibilitychange", apply);
    window.addEventListener("focus", apply);
    apply();

    return () => {
      window.clearInterval(tick);
      document.removeEventListener("visibilitychange", apply);
      window.removeEventListener("focus", apply);
      manager.off(MatrixRTCSessionManagerEvents.SessionStarted, onManagerSession);
      manager.off(MatrixRTCSessionManagerEvents.SessionEnded, onManagerSession);
      sessions.forEach((session, roomId) => {
        session.off(MatrixRTCSessionEvent.MembershipsChanged, apply);
        session.off(MatrixRTCSessionEvent.JoinStateChanged, apply);
        const room = client.getRoom(roomId);
        room?.off(RoomEvent.Timeline, apply);
      });
      stopRinging();
      closeNotification();
    };
  }, [client]);

  return incoming;
}

/** Called by hide, accept, and decline actions; end/expiry is handled by apply(). */
export function dismissIncomingCall(): void {
  stopRinging();
  closeNotification();
}

export async function declineIncomingCall(
  client: MatrixClient,
  call: IncomingCall,
): Promise<void> {
  dismissIncomingCall();
  const room = client.getRoom(call.roomId);
  if (room) await declineActiveCall(client, room);
}
